import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'use-intl';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, ExternalLink, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { KycRecord, KycProvider } from '@/clients/kycApiClient';
import { toast } from 'sonner';
import { onboardingService } from '@/services/onboardingService';

const SUPPORTED_COUNTRY_CODES = ['SE', 'NO', 'DK', 'FI'] as const;

interface KycModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  onKycCompleted?: () => void;
  /** Pre-select a provider. Defaults to 'openpayments'. Pass 'ztl' for ZTL onboarding. */
  provider?: KycProvider;
  /** Pre-select a KYC type. Defaults to 'payment'. Pass 'onboarding' for ZTL pre-flight onboarding. */
  kycType?: 'payment' | 'onboarding';
}

type KycStep = 'country_select' | 'initiating' | 'verification' | 'completed' | 'failed';

const KycModal: React.FC<KycModalProps> = ({
  open,
  onOpenChange,
  companyId,
  onKycCompleted,
  provider = 'openpayments',
  kycType = 'payment',
}) => {
  const t = useTranslations('consultantBank');
  // The latest translator, so the initiate/poll callbacks word their messages without being rebuilt.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const [currentStep, setCurrentStep] = useState<KycStep>('country_select');
  const [countryCode, setCountryCode] = useState<string>('SE');
  const [kycUrl, setKycUrl] = useState<string | null>(null);
  const [kycRecord, setKycRecord] = useState<KycRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep('country_select');
      setCountryCode('SE');
      setKycUrl(null);
      setKycRecord(null);
      setError(null);
      setLoading(false);
    } else {
      // Clear polling when modal closes
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [open]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const initiateKyc = useCallback(async (selectedCountry: string) => {
    if (!companyId) return;

    setLoading(true);
    setError(null);
    setCurrentStep('initiating');

    try {
      const kycService = ServiceRegistry.getKycService();

      // Check for an existing valid KYC of the same provider/type
      const existingKyc = await kycService.getLatestKyc(companyId, provider, kycType);

      if (existingKyc && kycService.isKycValid(existingKyc)) {
        setKycRecord(existingKyc);
        setCurrentStep('completed');
        setLoading(false);
        return;
      }

      const response = await kycService.createKyc(companyId, provider, kycType, selectedCountry);
      setKycUrl(response.url);

      // Move to verification step and show URL immediately
      setCurrentStep('verification');

      // Fetch the latest KYC record to get the ID for polling
      const latestKyc = await kycService.getLatestKyc(companyId, provider, kycType);
      if (latestKyc) {
        setKycRecord(latestKyc);
        startPolling(latestKyc.id);
      } else {
        throw new Error(tRef.current('kyc.errors.noRecord'));
      }
    } catch (err: unknown) {
      console.error('Failed to initiate KYC:', err);
      // Fixed copy, never err.message (FIN-380): a failed call's own text is either Supabase's
      // "non-2xx status code" or raw provider/DB detail. handleError() has already toasted the
      // server's sentence where one is safe to show, with its support reference.
      setError(tRef.current('kyc.errors.initiate'));
      setCurrentStep('failed');
    } finally {
      setLoading(false);
    }
  }, [companyId, provider, kycType]);

  const startPolling = useCallback((kycId: string) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const kycService = ServiceRegistry.getKycService();
        const updatedKyc = await kycService.getKyc(companyId, kycId);

        setKycRecord(updatedKyc);

        if (updatedKyc.status === 'completed') {
          setCurrentStep('completed');
          clearInterval(interval);
          setPollingInterval(null);
          toast.success(tRef.current('kyc.toasts.completed'));

          // Update onboarding completion status
          try {
            await onboardingService.updateKycCompletion(true);
            console.log('Onboarding KYC status updated to completed');
          } catch (error) {
            console.error('Failed to update onboarding KYC status:', error);
          }

          // Call completion callback after a short delay to show success state
          setTimeout(() => {
            onKycCompleted?.();
            onOpenChange(false);
          }, 2000);
        } else if (updatedKyc.status === 'failed' || updatedKyc.status === 'invalid') {
          setCurrentStep('failed');
          setError(tRef.current('kyc.errors.failed'));
          clearInterval(interval);
          setPollingInterval(null);
        }
      } catch (err) {
        console.error('Error polling KYC status:', err);
        // Don't stop polling on temporary errors, just log them
      }
    }, 5000); // Poll every 5 seconds

    setPollingInterval(interval);
  }, [companyId, onKycCompleted, onOpenChange]);

  const handleCountryConfirm = () => {
    initiateKyc(countryCode);
  };

  const handleRetry = () => {
    setCurrentStep('country_select');
    setError(null);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'country_select':
        return (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <h3 className="text-base font-medium">{t('kyc.countryTitle')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('kyc.countryBody')}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('kyc.country')}</label>
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_COUNTRY_CODES.map((code) => (
                    <SelectItem key={code} value={code}>{t(`countries.${code}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={handleCountryConfirm}>
              {t('kyc.continue')}
            </Button>
          </div>
        );

      case 'initiating':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <h3 className="text-lg font-medium">{t('kyc.initiatingTitle')}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {t('kyc.initiatingBody')}
            </p>
          </div>
        );

      case 'verification':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center space-x-2 text-primary">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">{t('kyc.waiting')}</span>
              </div>
            </div>

            {kycUrl && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-2">{t('kyc.completeTitle')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('kyc.completeBody')}
                  </p>
                </div>

                <div className="flex flex-col items-center space-y-4 py-4">
                  <Button
                    size="lg"
                    onClick={() => window.open(kycUrl, '_blank')}
                    className="w-full max-w-md"
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    {t('kyc.completeButton')}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    {t('kyc.completeHint')}
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 'completed':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <h3 className="text-lg font-medium text-green-700">{t('kyc.completedTitle')}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {t('kyc.completedBody')}
            </p>
          </div>
        );

      case 'failed':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <h3 className="text-lg font-medium text-red-700">{t('kyc.failedTitle')}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {error || t('kyc.failedFallback')}
            </p>
            <Button onClick={handleRetry} variant="outline">
              {t('kyc.tryAgain')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{t('kyc.title')}</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={loading && currentStep === 'initiating'}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="mt-4">
          {renderStepContent()}
        </div>

        {currentStep === 'verification' && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              {t.rich('kyc.note', { strong: (chunks) => <strong>{chunks}</strong> })}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KycModal;